import { ProcedureEntity } from "../../../domain/procedures/procedure-entity";
import { ProcedureQuotaEntity } from "../../../domain/procedures/procedure-quota-entity";
import apiClient from "../../../apis/api-client";
import { toast } from "react-toastify";
import {
  SmartApplyApiError,
  reportUnexpectedError,
  BaseApplicationService,
  ToastMessage,
} from "@graffer-inc/smart-apply";

export type ProcedureQuotaContextProps = {
  procedure: ProcedureEntity;
  procedureQuota: ProcedureQuotaEntity;
  isReachedQuota: boolean;
  isLoading: boolean;
  onMountSetting: typeof ProcedureQuotaService.prototype.onMountSetting;
  onChange: typeof ProcedureQuotaService.prototype.onChange;
  onSave: typeof ProcedureQuotaService.prototype.onSave;
};

export class ProcedureQuotaService extends BaseApplicationService<ProcedureQuotaContextProps> {
  state: ProcedureQuotaContextProps;

  constructor(arg: { procedure: ProcedureEntity }) {
    super();
    this.state = {
      ...arg,
      procedureQuota: ProcedureQuotaEntity.newFrom(arg.procedure),
      isReachedQuota: false,
      isLoading: false,
      onMountSetting: this.onMountSetting.bind(this),
      onChange: this.onChange.bind(this),
      onSave: this.onSave.bind(this),
    };
  }

  async onMountSetting() {
    try {
      this.setState({
        isLoading: true,
      });
      const { data } = await apiClient.fetchProcedureQuota(
        this.state.procedure.id,
      );
      const procedureQuota = ProcedureQuotaEntity.fromJSON({
        ...data,
      });
      this.setState({
        procedureQuota,
        isReachedQuota: data.createdAt && !(data.count < data.quota!), //serverから取得した状態を保持
      });
    } catch (e) {
      if (SmartApplyApiError.isErrorCode(e, "not_found")) {
        this.setState({
          procedureQuota: ProcedureQuotaEntity.newFrom(this.state.procedure),
        });
        return;
      }
      toast.error("申請数上限の取得に失敗しました。");
      reportUnexpectedError(e);
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  }

  async onChange(update: ProcedureQuotaEntity) {
    this.setState({
      procedureQuota: ProcedureQuotaEntity.fromJSON(update),
    });
  }
  async onSave() {
    if (!this.state.procedureQuota.quota) {
      await this.deleteQuota();
      return;
    }

    if (this.state.procedureQuota.createdAt) {
      await this.updateQuota();
    } else {
      await this.createQuota();
    }
  }

  private async createQuota() {
    if (!this.state.procedure.isDraft()) {
      toast.error(
        "手続きが公開済みの場合、申請数の上限を設定することはできません。",
      );
      return;
    }

    try {
      const { data: saved } = await apiClient.createProcedureQuota(
        this.state.procedure.id,
        this.state.procedureQuota.quota!,
      );
      this.setState({
        procedureQuota: ProcedureQuotaEntity.fromJSON({
          ...saved,
        }),
        isReachedQuota: saved.createdAt && !(saved.count < saved.quota!), //serverから取得した状態を保持
      });
      toast.success("手続き情報を更新しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      toast.error("手続きの更新に失敗しました。");
      reportUnexpectedError(e);
    }
  }
  private async updateQuota() {
    if (this.state.procedureQuota.quota! < this.state.procedureQuota.count) {
      toast.error("現在の申請件数以上の数値を入力してください。");
      return;
    }

    try {
      const { data: saved } = await apiClient.updateProcedureQuota(
        this.state.procedure.id,
        this.state.procedureQuota.quota!,
      );
      this.setState({
        procedureQuota: ProcedureQuotaEntity.fromJSON({
          ...saved,
        }),
        isReachedQuota: saved.createdAt && !(saved.count < saved.quota!), //serverから取得した状態を保持
      });
      toast.success("手続き情報を更新しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      toast.error("手続きの更新に失敗しました。");
      reportUnexpectedError(e);
    }
  }
  private async deleteQuota() {
    if (!this.state.procedure.isDraft()) {
      toast.error(
        "手続きが公開済みの場合、申請数の上限を解除することはできません。",
      );
      return;
    }

    try {
      await apiClient.deleteProcedureQuota(this.state.procedure.id);
      this.setState({
        procedureQuota: ProcedureQuotaEntity.fromJSON({
          ...ProcedureQuotaEntity.newFrom(this.state.procedure),
        }),
      });
      toast.success("手続き情報を更新しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      toast.error("手続きの更新に失敗しました。");
      reportUnexpectedError(e);
    }
  }
}

export const fetchProcedureQuota = async (
  procedureId: string,
): Promise<ProcedureQuotaEntity | null> => {
  try {
    const { data } = await apiClient.fetchProcedureQuota(procedureId);
    return data;
  } catch (e) {
    if (!SmartApplyApiError.isErrorCode(e, "not_found")) {
      reportUnexpectedError(e);
    }
    return null;
  }
};
